import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dica from '@/components/feature/Dica';
import BlocoBasico, { type BlocoBasicoHandle } from '@/pages/assistente-evento/components/BlocoBasico';
import PassoIngressos from './components/PassoIngressos';
import { useIngresso } from './useIngresso';

type Passo = 'evento' | 'ingressos';

interface Pendencia {
  chave: string;
  rotulo: string;
  pronta: boolean;
  aoClicar: () => void;
}

export default function CriarIngressoPage() {
  const navigate = useNavigate();
  const refBasico = useRef<BlocoBasicoHandle>(null);

  const {
    estado,
    erro,
    setErro,
    eventoId,
    draft,
    mudarDraft,
    perfilCidade,
    perfilUf,
    estadoSalvamento,
    salvando,
    ingressos,
    setIngressos,
    estadoIngressos,
    salvarIngressosAgora,
    salvarEvento,
    publicar,
  } = useIngresso();

  const [passo, setPasso] = useState<Passo>('evento');
  const [temRecebedor, setTemRecebedor] = useState(false);
  const [campoParaFocar, setCampoParaFocar] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);

  useEffect(() => {
    if (passo === 'evento' && campoParaFocar) {
      refBasico.current?.focar(campoParaFocar);
      setCampoParaFocar(null);
    }
  }, [passo, campoParaFocar]);

  const temIngressoComPreco = ingressos.some(
    (i) => i.nome.trim() && i.valorModo === 'pago' && i.valorCentavos > 0,
  );

  const pendencias: Pendencia[] = [
    {
      chave: 'nome',
      rotulo: 'Nome do evento',
      pronta: !!draft.nome.trim(),
      aoClicar: () => {
        setCampoParaFocar('nome');
        setPasso('evento');
      },
    },
    {
      chave: 'data',
      rotulo: 'Data e hora',
      pronta: !!(draft.inicioData && draft.inicioHora),
      aoClicar: () => {
        setCampoParaFocar('inicioData');
        setPasso('evento');
      },
    },
    {
      chave: 'local',
      rotulo: 'Local (cidade e UF)',
      pronta: !!(draft.cidade.trim() && draft.uf),
      aoClicar: () => {
        setCampoParaFocar('cidade');
        setPasso('evento');
      },
    },
    {
      chave: 'ingresso',
      rotulo: 'Pelo menos um ingresso com preço',
      pronta: temIngressoComPreco,
      aoClicar: () => {
        setPasso('ingressos');
        window.setTimeout(() => {
          document.getElementById('lista-ingressos')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      },
    },
    {
      chave: 'recebedor',
      rotulo: 'Para onde vai o dinheiro',
      pronta: temRecebedor,
      aoClicar: () => {
        setPasso('ingressos');
        window.setTimeout(() => {
          document.getElementById('bloco-recebedor')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      },
    },
  ];

  // Um só cálculo: a lista que aparece no bloco é a mesma que conta no rodapé.
  const faltaLista = pendencias.filter((p) => !p.pronta);
  const faltam = faltaLista.length;

  async function continuar() {
    if (passo === 'evento') {
      const ok = refBasico.current?.validar();
      if (!ok) return;
      const salvo = await salvarEvento();
      if (!salvo) return;
      setPasso('ingressos');
      window.scrollTo(0, 0);
    }
  }

  async function aoPublicar() {
    if (faltam > 0) return;
    setPublicando(true);
    const ok = await publicar();
    if (!ok) setPublicando(false);
  }

  if (estado === 'carregando') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-6 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (estado === 'erro') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] overflow-hidden rounded-lg border border-secondary-200 bg-white">
          <div className="h-[10px] w-full bg-primary-500" />
          <div className="p-8 text-center">
            <h1 className="text-xl font-normal text-foreground-950">Não foi possível carregar</h1>
            <p className="mt-2 text-sm text-secondary-500">{erro}</p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600"
            >
              Voltar aos meus eventos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <header className="fixed inset-x-0 top-0 z-20 border-b border-secondary-200 bg-white">
        <div className="mx-auto w-full max-w-[720px] px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="min-w-0 truncate text-sm font-medium text-foreground-950">
              {draft.nome.trim() || 'Novo evento'}
            </p>
            <button
              type="button"
              onClick={async () => {
                if (passo === 'ingressos') await salvarIngressosAgora();
                navigate('/meus-eventos');
              }}
              className="cursor-pointer whitespace-nowrap text-sm text-secondary-500 transition-colors hover:text-foreground-800"
            >
              Sair
            </button>
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <p className="min-w-0 text-xs text-secondary-500">
              Passo {passo === 'evento' ? 1 : 2} de 2 · {passo === 'evento' ? 'O evento' : 'Ingressos'}
            </p>
            <Dica id="criar.ingresso" />
          </div>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-secondary-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: passo === 'evento' ? '50%' : '100%' }}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[720px] px-4 pb-40 pt-24">
        {erro && (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
            {erro}
            <button
              type="button"
              onClick={() => setErro(null)}
              className="ml-2 cursor-pointer font-medium underline"
            >
              Fechar
            </button>
          </div>
        )}

        {passo === 'evento' ? (
          <BlocoBasico
            ref={refBasico}
            draft={draft}
            eventoId={eventoId}
            onChange={mudarDraft}
            perfilCidade={perfilCidade}
            perfilUf={perfilUf}
            estadoSalvamento={estadoSalvamento}
            ehIngresso
          />
        ) : (
          <>
            {faltam > 0 && (
              <section className="mb-6 overflow-hidden rounded-lg border border-secondary-200 bg-white">
                <h4 className="border-b border-secondary-100 px-5 py-3 text-sm font-medium text-foreground-950">
                  Para publicar, falta
                </h4>
                <ul>
                  {faltaLista.map((p, i) => (
                    <li key={p.chave} className={i > 0 ? 'border-t border-secondary-100' : ''}>
                      <button
                        type="button"
                        onClick={p.aoClicar}
                        className="flex w-full cursor-pointer items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-background-50"
                      >
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-amber-500">
                          <i className="ri-alert-line" />
                        </span>
                        <span className="text-sm text-foreground-900">{p.rotulo}</span>
                        <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-sm text-primary-600">
                          resolver
                          <span className="flex h-4 w-4 items-center justify-center">
                            <i className="ri-arrow-right-line" />
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <PassoIngressos
              ingressos={ingressos}
              eventoId={eventoId}
              onChange={setIngressos}
              onTemRecebedor={setTemRecebedor}
            />
          </>
        )}
      </main>

      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-secondary-200 bg-white px-4 py-3">
        <div className="mx-auto w-full max-w-[720px]">
          {passo === 'ingressos' && estadoIngressos !== 'ocioso' && (
            <p className="mb-2 flex items-center gap-1.5 text-xs text-secondary-500">
              {estadoIngressos === 'salvando' && 'Salvando…'}
              {estadoIngressos === 'salvo' && (
                <>
                  <i className="ri-check-line" />
                  Salvo
                </>
              )}
              {estadoIngressos === 'offline' && 'Sem conexão. Vou tentar de novo.'}
            </p>
          )}
          <div className="flex items-center gap-3">
            {passo === 'ingressos' && (
              <button
                type="button"
                onClick={async () => {
                  await salvarIngressosAgora();
                  setPasso('evento');
                  window.scrollTo(0, 0);
                }}
                disabled={salvando || publicando}
                className="cursor-pointer whitespace-nowrap rounded-md px-4 py-3 text-[15px] font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Voltar
              </button>
            )}
            {passo === 'evento' ? (
              <button
                type="button"
                onClick={continuar}
                disabled={salvando}
                className="flex h-12 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
              >
                {salvando ? 'Salvando…' : 'Continuar'}
              </button>
            ) : (
              <button
                type="button"
                onClick={aoPublicar}
                disabled={salvando || publicando || faltam > 0}
                className="flex h-12 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
              >
                {publicando ? 'Publicando…' : 'Publicar evento'}
              </button>
            )}
          </div>
          {passo === 'ingressos' && faltam > 0 && !publicando && (
            <p className="mt-2 text-center text-sm text-foreground-700">
              Falta {faltam} {faltam === 1 ? 'coisa' : 'coisas'} para colocar no ar.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}